export type CashBoxAttemptSource =
  | 'CASCADE'
  | 'PERSONAL'
  | 'DICTATED'
  | 'REQUISITE_NAMED'
  | 'SMS'
  | 'REASSIGNED'
  | 'LEGACY'

export type CashBoxAttemptState = 'SKIPPED' | 'OPEN' | 'COMPLETED' | 'DECLINED'

export interface CashBoxAttemptUser {
  id?: number | null
  uuid?: string | null
}

export interface CashBoxAttempt {
  cashBox: { id: number | null; title: string } | null
  source: CashBoxAttemptSource
  state: CashBoxAttemptState
  reason: { code: string; label: string } | null
  takenBy: CashBoxAttemptUser | null
  closedBy: CashBoxAttemptUser | null
  openedAt: string
  closedAt: string | null
  detail: string | null
}

export const CASH_BOX_ATTEMPT_SOURCE_LABELS: Record<CashBoxAttemptSource, string> = {
  CASCADE: 'Каскад',
  PERSONAL: 'Персональная',
  DICTATED: 'Назначена вручную',
  REQUISITE_NAMED: 'По реквизиту',
  SMS: 'По СМС',
  REASSIGNED: 'Переназначена',
  LEGACY: 'Из истории',
}

export const CASH_BOX_ATTEMPT_STATE_LABELS: Record<CashBoxAttemptState, string> = {
  SKIPPED: 'Пропущена',
  OPEN: 'В работе',
  COMPLETED: 'Завершена',
  DECLINED: 'Отклонена',
}

/**
 * Badge and row classes per state. Neutral states avoid `badge-light-secondary` (contrast 1.22:1 in the
 * light theme, 1.36:1 in the dark one); `badge-light` with gray text keeps contrast in both themes.
 */
export const CASH_BOX_ATTEMPT_STATE_TONE: Record<
  CashBoxAttemptState,
  { badge: string; row: string }
> = {
  OPEN: { badge: 'badge badge-light-primary fw-bold', row: 'fw-semibold' },
  SKIPPED: { badge: 'badge badge-light text-gray-700', row: 'text-muted' },
  COMPLETED: { badge: 'badge badge-light-success', row: '' },
  DECLINED: { badge: 'badge badge-light-danger', row: '' },
}

export const cashBoxAttemptSourceLabel = (source: string): string =>
  CASH_BOX_ATTEMPT_SOURCE_LABELS[source as CashBoxAttemptSource] ?? source

export const cashBoxAttemptUserLabel = (user: CashBoxAttemptUser | null | undefined): string | null =>
  user ? user.uuid ?? (user.id != null ? `#${user.id}` : null) : null

/**
 * A local adapter's transaction row, as received by the staff who see cassa attempts (the list is
 * null on another adapter's row). Its payment id is the cassa entry id, not a provider operation id,
 * so it has nothing to look up in the integration audit log.
 */
export const isCashBoxRow = (row: { cashBoxAttempts?: CashBoxAttempt[] | null }): boolean =>
  Array.isArray(row.cashBoxAttempts)
